import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock3, Loader2, Undo2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ChoiceButton } from "@/components/ChoiceButton";
import { findSimilar } from "@/components/IdentifyPickField";

// NAME A TAG (his calls, 21 + 23 Oct): tap a name. Names come from the managed names list, in his order — already
// used for these numbers · closest to the AI's words · common components. Typing is last, and a typed name goes
// through the same "we already call this X" check first. A name that isn't on the list is SUGGESTED with one tap:
// the tag uses it now, marked "name waiting for approval", and a list admin approves or merges it later.
const slug = (n) => n.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase();
const Chips = ({ title, items, testid, onPick, busy, waiting }) => (items || []).length ? (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-3 mb-1">{title}</div>
    <div className="flex flex-wrap gap-1.5">
      {items.map((n) => (
        <ChoiceButton key={n} tone={waiting ? "amber" : "plain"} disabled={busy} data-testid={`${testid}-${slug(n)}`}
          onClick={() => onPick(n, waiting)} className="rounded-full text-sm font-medium">
          {waiting && <Clock3 className="w-3 h-3 mr-1" />}{n}
        </ChoiceButton>
      ))}
    </div>
  </div>
) : null;

function TypedName({ draft, setDraft, names, busy, canApproveNames, onListed, onAdd, onSuggest }) {
  const sim = useMemo(() => findSimilar(draft, names), [draft, names]);
  const typed = draft.trim();
  return (
    <div className="space-y-2 mt-1">
      <Input data-testid="tag-heading-input" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type the name" className="rounded-lg h-11" />
      {sim.exact && (
        <div data-testid="tag-heading-exact" className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-[13px] text-amber-900">
          It's already on the list as <b>{sim.exact.label}</b>.
          <ChoiceButton tone="go" data-testid="tag-heading-use-exact" disabled={busy} onClick={() => onListed(sim.exact.label, "used_exact", sim)} className="ml-2">Use {sim.exact.label}</ChoiceButton>
        </div>
      )}
      {!sim.exact && sim.near.length > 0 && (
        <div data-testid="tag-heading-similar" className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-[13px] text-amber-900">
          We already call this <b>{sim.near[0].label}</b>. Use that?
          <div className="flex flex-wrap gap-1.5 mt-2">
            {sim.near.slice(0, 3).map((o) => (
              <ChoiceButton key={o.value} tone="go" disabled={busy} data-testid={`tag-heading-use-${o.value}`} onClick={() => onListed(o.label, "picked_existing", sim)}>Use {o.label}</ChoiceButton>
            ))}
          </div>
        </div>
      )}
      {typed && !sim.exact && (canApproveNames ? (
        <ChoiceButton data-testid="tag-heading-add-new" disabled={busy} onClick={() => onAdd(typed, sim)}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : sim.near.length ? `No, add “${typed}” as a new name` : `Add “${typed}” to the names list and use it`}
        </ChoiceButton>
      ) : (
        <div className="space-y-1">
          <ChoiceButton tone="amber" data-testid="tag-heading-suggest" disabled={busy} onClick={() => onSuggest(typed, sim)}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Clock3 className="w-3.5 h-3.5 mr-1" />{sim.near.length ? `No, suggest “${typed}” and use it now` : `Suggest “${typed}” and use it now`}</>}
          </ChoiceButton>
          <p data-testid="tag-heading-suggest-help" className="text-[11px] text-slate-500">The tag uses it straight away, marked “name waiting for approval”. A list admin approves it or merges it into a name on the list — every tag using it follows.</p>
        </div>
      ))}
    </div>
  );
}

export function TagHeadingRename({ open, onOpenChange, partId, returnId, gi, current, was, aiWords, canApproveNames, brand: brandIn, onRenamed }) {
  const [sugg, setSugg] = useState(null);
  const [names, setNames] = useState([]);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState("");

  useEffect(() => {
    if (!open) return;
    setTyping(false); setDraft(""); setSugg(null); setBrand(brandIn || "");
    api.get(`/parts/${partId}/ai-research/${returnId}/heading-suggestions`, { params: { group_index: gi } })
      .then(({ data }) => { setSugg(data); if (!brandIn && data.brand) setBrand(data.brand); })
      .catch(() => setSugg({ used_for_numbers: [], closest: [], common: [], waiting: [] }));
    api.get("/managed-lists/names").then(({ data }) => setNames((data.items || []).map((x) => ({ value: x.id, label: x.name })))).catch(() => {});
  }, [open, partId, returnId, gi, brandIn]);

  const send = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${partId}/ai-research/${returnId}/heading`, { group_index: gi, brand, ...body });
      onRenamed(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't name the tag."); }
    setBusy(false);
  };
  const rename = (name, waiting) => send(waiting ? { name, suggest: true } : { name });
  const nearMiss = (sim, chosen, resolution) => api.post("/taxonomy/near-miss", { field: "name", typed: draft.trim(), matched: sim.near.map((n) => n.label), chosen, resolution }).catch(() => {});
  const onListed = (label, resolution, sim) => { nearMiss(sim, label, resolution); rename(label); };
  const onSuggest = (name, sim) => { if (sim.near.length) nearMiss(sim, name, "suggested_new_despite_similar"); send({ name, suggest: true }); };
  const onAdd = async (name, sim) => {
    setBusy(true);
    try {
      const { data } = await api.post("/managed-lists/names", { name });
      if (sim.near.length) nearMiss(sim, name, "created_new_despite_similar");
      setBusy(false);
      await rename(data.item.name);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add the name."); setBusy(false); }
  };
  const empty = sugg && !sugg.used_for_numbers.length && !sugg.closest.length && !sugg.common.length && !(sugg.waiting || []).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tag-heading-dialog" className="rounded-2xl sm:max-w-md">
        <DialogTitle>What is this tag on?</DialogTitle>
        <DialogDescription className="text-xs text-slate-500 -mt-1">Now: <b>{current}</b>. Tap a name — it's saved on this tag and the numbers placed from it follow.</DialogDescription>
        {brand && (
          <div data-testid="tag-heading-brand" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-slate-600">
            <span>Brand: <b className="whitespace-nowrap">{brand}</b></span>
            <span className="text-slate-500">from our brands list — it goes onto this tag's numbers</span>
            <ChoiceButton data-testid="tag-heading-brand-clear" onClick={() => setBrand("")} className="h-7 min-h-7 px-2"><X className="w-3 h-3 mr-1" />No brand</ChoiceButton>
          </div>
        )}
        {!sugg ? <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <div data-testid="tag-heading-suggestions">
            <Chips title="Already used for these numbers" items={sugg.used_for_numbers} testid="tag-heading-used" onPick={rename} busy={busy} />
            <Chips title="From our names list: closest to the AI's words" items={sugg.closest} testid="tag-heading-closest" onPick={rename} busy={busy} />
            <Chips title="Common components" items={sugg.common} testid="tag-heading-common" onPick={rename} busy={busy} />
            <Chips title="Suggested by the crew, waiting for approval" items={sugg.waiting} testid="tag-heading-waiting" onPick={rename} busy={busy} waiting />
            {empty && <p data-testid="tag-heading-no-suggestions" className="text-[12px] text-slate-500 mt-3">No names on the list match this tag yet.</p>}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {was && was !== current && (
            <ChoiceButton tone="danger" data-testid="tag-heading-put-back" disabled={busy} onClick={() => send({ name: was, suggest: true })}>
              <Undo2 className="w-3.5 h-3.5 mr-1" />Put back “{was}”
            </ChoiceButton>
          )}
          {aiWords && aiWords !== current && aiWords !== was && (
            <ChoiceButton data-testid="tag-heading-ai-words" disabled={busy} onClick={() => send({ name: aiWords, put_back: true })}>
              <Undo2 className="w-3.5 h-3.5 mr-1" />Back to the AI's words: “{aiWords}”
            </ChoiceButton>
          )}
          {!typing && (
            <ChoiceButton data-testid="tag-heading-type-toggle" onClick={() => setTyping(true)}>None of these: type a name</ChoiceButton>
          )}
        </div>
        {typing && <TypedName draft={draft} setDraft={setDraft} names={names} busy={busy} canApproveNames={canApproveNames}
          onListed={onListed} onAdd={onAdd} onSuggest={onSuggest} />}
      </DialogContent>
    </Dialog>
  );
}
